import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/api";

const HERO_BASE =
  "https://images.unsplash.com/photo-1776196463688-5f21c9632c75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHx3YXRlciUyMHdlbGwlMjBkcmlsbGluZyUyMHJpZyUyMHdvcmtlciUyMGNvbnN0cnVjdGlvbiUyMG1hY2hpbmV8ZW58MHx8fHwxNzkxOTg3ODk1fDA&ixlib=rb-4.1.0&q=85&auto=format&fit=crop";
const heroSrc = (w) => `${HERO_BASE}&w=${w}`;
const HERO_SRC_SET = [800, 1600, 2400].map((w) => `${heroSrc(w)} ${w}w`).join(", ");

const revealLine = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.18 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const contentFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} id="top-hero" className="relative flex min-h-[100svh] items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={heroSrc(1600)}
          srcSet={HERO_SRC_SET}
          sizes="100vw"
          fetchPriority="high"
          alt="Utilaj de foraj puțuri de apă în lucru"
          className="h-[115%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-hydro-navy/60 via-hydro-navy/55 to-hydro-navy" />
        <div className="absolute inset-0 bg-gradient-to-r from-hydro-navy/90 via-hydro-navy/45 to-transparent" />
      </motion.div>

      {/* depth ruler — signature detail */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-8 top-28 bottom-28 hidden xl:flex flex-col justify-between items-end"
      >
        {[150, 125, 100, 75, 50, 25].map((m) => (
          <div key={m} className="flex items-center gap-2 font-mono text-[11px] text-white/40">
            <span>{m} m</span>
            <span className="block h-px w-8 bg-white/30" />
          </div>
        ))}
      </div>

      <motion.div
        style={{ opacity: contentFade }}
        className="relative mx-auto w-full max-w-7xl px-5 pb-24 pt-36 sm:px-8"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.05 }}
          className="font-mono text-[11px] uppercase tracking-[0.32em] text-hydro-cyan sm:text-xs"
        >
          Foraje puțuri de apă · București &amp; Ilfov
        </motion.p>

        <h1 className="mt-6 font-display font-black leading-[0.95] tracking-tight text-[clamp(2.9rem,8.5vw,6.8rem)] text-white">
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              variants={revealLine}
              initial="hidden"
              animate="show"
              custom={0}
            >
              Apă curată,
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span
              className="block"
              variants={revealLine}
              initial="hidden"
              animate="show"
              custom={1}
            >
              la <span className="text-hydro-cyan">adâncimea</span> ta.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg"
        >
          Forăm puțuri de apă în București și Ilfov, cu tubulatură PVC albastră cu filet.
          Îți calculezi prețul orientativ în 30 de secunde — direct pe site.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href="#calculator"
            data-testid="hero-cta-calculate"
            className="group inline-flex items-center gap-2.5 rounded-full bg-hydro-cyan px-7 py-4 text-base font-bold text-hydro-navy transition-all hover:bg-white"
          >
            Calculează prețul
            <ArrowDown size={18} className="transition-transform group-hover:translate-y-0.5" />
          </a>
          <a
            href={PHONE_TEL}
            data-testid="hero-cta-call"
            className="inline-flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-4 text-base font-bold text-white transition-colors hover:border-hydro-cyan hover:text-hydro-cyan"
          >
            <Phone size={17} />
            {PHONE_DISPLAY}
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.05 }}
          className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-white/45"
        >
          Adâncimi 10–150 m · Ofertă pe loc · Toate localitățile din Ilfov
        </motion.p>
      </motion.div>
    </section>
  );
}
